import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCreateActual, useUpdateActual, type ItineraryActual } from "@/hooks/useItineraryActuals";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  leadId: string;
  itineraryItemId: string;
  defaultCurrency: string;
  defaultSupplier?: string | null;
  existing?: ItineraryActual | null;
}

export function LogPaymentDialog({
  open, onOpenChange, leadId, itineraryItemId, defaultCurrency, defaultSupplier, existing,
}: Props) {
  const create = useCreateActual(leadId);
  const update = useUpdateActual(leadId);

  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState(defaultCurrency || "ZAR");
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10));
  const [supplier, setSupplier] = useState(defaultSupplier || "");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [attachment, setAttachment] = useState("");

  useEffect(() => {
    if (existing) {
      setAmount(String(existing.amount));
      setCurrency(existing.currency);
      setPaidOn(existing.paid_on);
      setSupplier(existing.supplier_name || "");
      setReference(existing.reference || "");
      setNotes(existing.notes || "");
      setAttachment(existing.attachment_url || "");
    } else if (open) {
      setAmount("");
      setCurrency(defaultCurrency || "ZAR");
      setPaidOn(new Date().toISOString().slice(0, 10));
      setSupplier(defaultSupplier || "");
      setReference("");
      setNotes("");
      setAttachment("");
    }
  }, [existing, open, defaultCurrency, defaultSupplier]);

  const submit = async () => {
    const num = parseFloat(amount);
    if (!num || num <= 0) return;
    const payload = {
      itinerary_item_id: itineraryItemId,
      amount: num,
      currency,
      paid_on: paidOn,
      supplier_name: supplier || null,
      reference: reference || null,
      notes: notes || null,
      attachment_url: attachment || null,
    };
    if (existing) {
      await update.mutateAsync({ id: existing.id, ...payload });
    } else {
      await create.mutateAsync(payload);
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{existing ? "Edit payment" : "Log supplier payment"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <Label>Amount</Label>
              <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            <div>
              <Label>Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ZAR">ZAR</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Paid on</Label>
            <Input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
          </div>
          <div>
            <Label>Supplier</Label>
            <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="Supplier name" />
          </div>
          <div>
            <Label>Reference</Label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Invoice / payment ref" />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
          <div>
            <Label>Attachment URL (optional)</Label>
            <Input value={attachment} onChange={(e) => setAttachment(e.target.value)} placeholder="https://…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={create.isPending || update.isPending}>
            {existing ? "Save" : "Log payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}